import React, { useEffect, useMemo, useRef, useState } from 'react';
import ArticleCard from '../components/ArticleCard';
import SectionBlock from '../components/SectionBlock';
import SidebarBlock from '../components/SidebarBlock';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';
import { getAllPosts, getCategoryList, getFeaturedPosts, getLatestPosts, getPostsByCategory } from '../content/config';
import { getCategoryBySlug } from '../content/categories';

const Home = () => {
  const SWIPE_HINT_STORAGE_KEY = 'lmdSwipeHintSeen';
  const [activeFilter, setActiveFilter] = useState('Toutes');
  const [tickerIndex, setTickerIndex] = useState(0);
  const [isTickerPaused, setIsTickerPaused] = useState(false);
  const [isManualPaused, setIsManualPaused] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [dynamicFill, setDynamicFill] = useState({ left: 4, center: 8 });
  const manualPauseTimeoutRef = useRef(null);
  const touchStartXRef = useRef(null);
  const touchCurrentXRef = useRef(null);
  const leftColumnRef = useRef(null);
  const centerColumnRef = useRef(null);
  const rightColumnRef = useRef(null);
  const posts = getAllPosts();

  const categoryFilters = useMemo(
    () => ['Toutes', ...getCategoryList().filter((category) => category.slug !== 'a-la-une').map((category) => category.label)],
    []
  );

  const filteredArticles = useMemo(() => {
    if (activeFilter === 'Toutes') return posts;
    const category = getCategoryList().find((item) => item.label === activeFilter);
    if (!category) return posts;
    return posts.filter((article) => article.category === category.slug);
  }, [activeFilter, posts]);

  const featuredPosts = getFeaturedPosts();
  const latestPosts = getLatestPosts(20);
  const fallbackArticles = filteredArticles.length ? filteredArticles : posts;
  const centreSections = [
    'ia',
    'business-opportunites',
    'aides-financements',
    'bons-plans',
    'marketing-seo'
  ];
  const homeLayout = useMemo(() => {
    const used = new Set();
    const takeUnique = (items, count, predicate = () => true) => {
      const output = [];
      for (const item of items) {
        if (!item?.slug || used.has(item.slug)) continue;
        if (!predicate(item)) continue;
        used.add(item.slug);
        output.push(item);
        if (output.length >= count) break;
      }
      return output;
    };

    const heroPick = takeUnique([...featuredPosts, ...fallbackArticles], 1)[0] || null;
    const alaUnePick = takeUnique(fallbackArticles, 3);
    const actuRapidePick = takeUnique(fallbackArticles, 4, (article) => article.category === 'actu-rapide');
    const analysesPick = takeUnique(fallbackArticles, 3, (article) => article.category === 'analyses-opinions');
    const centrePick = Object.fromEntries(
      centreSections.map((slug) => [slug, takeUnique(fallbackArticles, 3, (article) => article.category === slug)])
    );
    const livePick = takeUnique(latestPosts, 6);
    const populairesPick = takeUnique([...featuredPosts, ...latestPosts, ...fallbackArticles], 5);
    const dealsPick = takeUnique(getPostsByCategory('bons-plans'), 4);
    const tickerPick = takeUnique(fallbackArticles, 8);

    return {
      hero: heroPick,
      alaUne: alaUnePick,
      actuRapide: actuRapidePick,
      analyses: analysesPick,
      centreSectionArticles: centrePick,
      liveFeed: livePick.map((item, idx) => ({
        ...item,
        time: `${9 + idx}h${idx % 2 === 0 ? '00' : '30'}`
      })),
      populaires: populairesPick,
      deals: dealsPick,
      tickerItems: tickerPick
    };
  }, [featuredPosts, fallbackArticles, latestPosts, centreSections]);

  const { hero, alaUne, actuRapide, analyses, centreSectionArticles, liveFeed, populaires, deals, tickerItems } = homeLayout;
  const tags = [...new Set(fallbackArticles.flatMap((article) => article.tags))].slice(0, 14);
  const complementaryArticles = useMemo(() => {
    const usedSlugs = new Set();
    const register = (items) => {
      items.forEach((item) => {
        if (item?.slug) usedSlugs.add(item.slug);
      });
    };

    register(hero ? [hero] : []);
    register(alaUne);
    register(actuRapide);
    register(analyses);
    register(Object.values(centreSectionArticles).flat());
    register(populaires);
    register(deals);
    register(liveFeed);

    return fallbackArticles.filter((article) => !usedSlugs.has(article.slug));
  }, [hero, alaUne, actuRapide, analyses, centreSectionArticles, populaires, deals, liveFeed, fallbackArticles]);
  const leftPool = useMemo(() => complementaryArticles.filter((_, index) => index % 2 === 0), [complementaryArticles]);
  const centerPool = useMemo(() => complementaryArticles.filter((_, index) => index % 2 !== 0), [complementaryArticles]);
  const leftComplementary = leftPool.slice(0, dynamicFill.left);
  const centerComplementary = centerPool.slice(0, dynamicFill.center);
  const isTickerAutoRunning = !isTickerPaused && !isManualPaused && tickerItems.length > 1;

  const registerManualInteraction = () => {
    setIsManualPaused(true);
    if (showSwipeHint) {
      setShowSwipeHint(false);
      window.localStorage.setItem(SWIPE_HINT_STORAGE_KEY, 'true');
    }
    if (manualPauseTimeoutRef.current) {
      clearTimeout(manualPauseTimeoutRef.current);
    }
    manualPauseTimeoutRef.current = setTimeout(() => {
      setIsManualPaused(false);
    }, 2000);
  };

  const moveTicker = (direction) => {
    if (!tickerItems.length) return;
    registerManualInteraction();
    if (direction === 'next') {
      setTickerIndex((prev) => (prev + 1) % tickerItems.length);
      return;
    }
    setTickerIndex((prev) => (prev - 1 + tickerItems.length) % tickerItems.length);
  };

  const handleTickerTouchStart = (event) => {
    if (!event.touches?.length) return;
    if (showSwipeHint) {
      setShowSwipeHint(false);
      window.localStorage.setItem(SWIPE_HINT_STORAGE_KEY, 'true');
    }
    touchStartXRef.current = event.touches[0].clientX;
    touchCurrentXRef.current = event.touches[0].clientX;
    setIsTickerPaused(true);
  };

  const handleTickerTouchMove = (event) => {
    if (!event.touches?.length) return;
    touchCurrentXRef.current = event.touches[0].clientX;
  };

  const handleTickerTouchEnd = () => {
    if (touchStartXRef.current === null || touchCurrentXRef.current === null) {
      setIsTickerPaused(false);
      return;
    }

    const deltaX = touchCurrentXRef.current - touchStartXRef.current;
    const swipeThreshold = 45;

    if (Math.abs(deltaX) >= swipeThreshold) {
      moveTicker(deltaX < 0 ? 'next' : 'prev');
    }

    touchStartXRef.current = null;
    touchCurrentXRef.current = null;
    setIsTickerPaused(false);
  };

  useEffect(() => {
    setTickerIndex(0);
  }, [activeFilter]);

  useEffect(() => {
    if (!isTickerAutoRunning) return undefined;

    const intervalId = setInterval(() => {
      setTickerIndex((current) => (current + 1) % tickerItems.length);
    }, 4000);

    return () => clearInterval(intervalId);
  }, [isTickerAutoRunning, tickerItems.length]);

  useEffect(() => {
    const hasSeenHint = window.localStorage.getItem(SWIPE_HINT_STORAGE_KEY) === 'true';
    if (!hasSeenHint) {
      setShowSwipeHint(true);
    }
  }, []);

  useEffect(() => {
    if (!showSwipeHint) return undefined;

    const hintTimeoutId = setTimeout(() => {
      setShowSwipeHint(false);
      window.localStorage.setItem(SWIPE_HINT_STORAGE_KEY, 'true');
    }, 4500);

    return () => {
      clearTimeout(hintTimeoutId);
    };
  }, [showSwipeHint]);

  useEffect(() => {
    return () => {
      if (manualPauseTimeoutRef.current) {
        clearTimeout(manualPauseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const leftEl = leftColumnRef.current;
    const centerEl = centerColumnRef.current;
    const rightEl = rightColumnRef.current;
    if (!leftEl || !centerEl || !rightEl) return undefined;

    const DENSE_CARD_ESTIMATED_HEIGHT = 86;
    const CENTER_ROW_ESTIMATED_HEIGHT = 96;
    const MIN_LEFT_CARDS = 4;
    const MIN_CENTER_CARDS = 8;

    const updateFill = () => {
      const rightHeight = rightEl.getBoundingClientRect().height;
      const leftHeight = leftEl.getBoundingClientRect().height;
      const centerHeight = centerEl.getBoundingClientRect().height;

      const leftMissing = Math.max(0, rightHeight - leftHeight);
      const centerMissing = Math.max(0, rightHeight - centerHeight);

      const leftExtra = Math.ceil(leftMissing / DENSE_CARD_ESTIMATED_HEIGHT);
      const centerRows = Math.ceil(centerMissing / CENTER_ROW_ESTIMATED_HEIGHT);

      const nextLeft = Math.min(leftPool.length, Math.max(MIN_LEFT_CARDS, leftExtra));
      const nextCenter = Math.min(centerPool.length, Math.max(MIN_CENTER_CARDS, centerRows * 2));

      setDynamicFill((prev) => {
        if (prev.left === nextLeft && prev.center === nextCenter) return prev;
        return { left: nextLeft, center: nextCenter };
      });
    };

    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(updateFill);
    });

    observer.observe(leftEl);
    observer.observe(centerEl);
    observer.observe(rightEl);
    window.addEventListener('resize', updateFill);
    window.requestAnimationFrame(updateFill);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateFill);
    };
  }, [leftPool.length, centerPool.length, activeFilter]);

  return (
    <div className="bg-slate-50">
      <Seo
        title="Accueil"
        description="LMD - Le Vrai Journal du Digital. Le digital, expliqué autrement."
        path="/"
        image={hero?.cover}
      />
      <div className="mx-auto max-w-[1400px] px-4 py-6 md:py-8">
        <div
          className="mb-4 rounded-lg border border-slate-300 bg-white"
          onMouseEnter={() => setIsTickerPaused(true)}
          onMouseLeave={() => setIsTickerPaused(false)}
          onTouchStart={handleTickerTouchStart}
          onTouchMove={handleTickerTouchMove}
          onTouchEnd={handleTickerTouchEnd}
          onTouchCancel={handleTickerTouchEnd}
        >
          {showSwipeHint && (
            <div className="pointer-events-none px-3 pt-2 md:hidden">
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Swipe pour naviguer
              </span>
            </div>
          )}
          <div className="flex items-center gap-3 border-b border-slate-200 px-3 py-2">
            <span className="rounded bg-rose-600 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-white">
              Breaking
            </span>
            <p className="line-clamp-1 text-sm font-medium text-slate-700">
              {tickerItems[tickerIndex]?.title || 'Mise a jour en cours...'}
            </p>
            <span className="hidden text-[10px] font-semibold uppercase tracking-wider text-slate-400 md:inline">
              {isTickerPaused || isManualPaused ? 'Pause' : 'Auto'}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => moveTicker('prev')}
                className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:border-slate-300"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => moveTicker('next')}
                className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:border-slate-300"
              >
                Next
              </button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto px-3 py-2">
            {tickerItems.map((item, idx) => (
              <button
                key={`ticker-${item.slug}`}
                type="button"
                onClick={() => {
                  registerManualInteraction();
                  setTickerIndex(idx);
                }}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold transition ${
                  idx === tickerIndex ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {item.category}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6 flex items-center justify-between border-b border-slate-300 pb-3">
          <h1 className="lmd-editorial-title text-3xl font-extrabold text-slate-900 md:text-4xl">
            Edition du jour
          </h1>
          <span className="hidden text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 md:block">
            Le digital, expliqué autrement.
          </span>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          {categoryFilters.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setActiveFilter(option)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition ${
                activeFilter === option
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-slate-300 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-600'
              }`}
            >
              {option}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-12">
          <section ref={leftColumnRef} className="space-y-6 md:col-span-1 xl:col-span-3">
            <SectionBlock title="À la une" articles={alaUne} dense />
            <SectionBlock title="Actu rapide" articles={actuRapide} dense />
            <SectionBlock title="Analyses & opinions" articles={analyses} dense />
            {leftComplementary.length > 0 ? (
              <section className="rounded-xl border border-slate-200 bg-white p-4 md:p-5">
                <div className="mb-3 border-b border-slate-200 pb-2">
                  <h2 className="text-sm font-black uppercase tracking-widest text-slate-900">Complement en continu</h2>
                </div>
                {leftComplementary.map((article) => (
                  <ArticleCard key={`left-extra-${article.slug}`} article={article} variant="denseCompact" />
                ))}
              </section>
            ) : null}
          </section>

          <main ref={centerColumnRef} className="lmd-column-rule space-y-6 md:col-span-1 md:pl-6 xl:col-span-6 xl:pr-6">
            <div className="lmd-kicker">A la Une</div>
            {hero ? (
              <ArticleCard article={hero} variant="hero" categoryOverride="À la une" />
            ) : (
              <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
                Aucun article pour ce filtre.
              </div>
            )}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {centreSections.map((title) => (
                <SectionBlock
                  key={title}
                  title={getCategoryBySlug(title)?.label || title}
                  articles={centreSectionArticles[title] || []}
                  dense
                />
              ))}
            </div>
            {centerComplementary.length > 0 ? (
              <section className="rounded-xl border border-slate-200 bg-white p-4 md:p-5">
                <div className="mb-3 flex items-center justify-between border-b border-slate-200 pb-2">
                  <h2 className="text-sm font-black uppercase tracking-widest text-slate-900">Articles complementaires</h2>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Edition du jour</span>
                </div>
                <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
                  {centerComplementary.map((article) => (
                    <ArticleCard key={`center-extra-${article.slug}`} article={article} variant="denseCompact" />
                  ))}
                </div>
              </section>
            ) : null}
          </main>

          <aside ref={rightColumnRef} className="lmd-column-rule space-y-6 md:col-span-2 md:pl-6 xl:col-span-3">
            <SidebarBlock title="Live" tone="dark">
              <div className="space-y-3">
                {liveFeed.map((item) => (
                  <div key={`live-${item.slug}`} className="flex items-start gap-3 border-b border-white/10 pb-3 last:border-b-0">
                    <span className="rounded bg-rose-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                      {item.time}
                    </span>
                    <p className="text-sm leading-snug text-slate-100">{item.title}</p>
                  </div>
                ))}
              </div>
            </SidebarBlock>

            <SidebarBlock title="Populaires">
              <div className="space-y-4">
                {populaires.map((article, index) => (
                  <div key={article.slug} className="flex gap-3 border-b border-slate-100 pb-3 last:border-b-0">
                    <span className="text-xl font-black text-indigo-300">0{index + 1}</span>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-slate-400">{article.categoryLabel}</p>
                      <h4 className="text-sm font-semibold leading-snug text-slate-900">{article.title}</h4>
                    </div>
                  </div>
                ))}
              </div>
            </SidebarBlock>

            <SidebarBlock title="Derniers bons plans" tone="highlight">
              <div className="space-y-3">
                {deals.map((article) => (
                  <ArticleCard key={article.slug} article={article} variant="compact" />
                ))}
              </div>
            </SidebarBlock>

            <SidebarBlock title="Newsletter">
              <p className="mb-3 text-sm text-slate-600">
                Recois chaque matin le condensé des tendances digitales, outils et opportunites.
              </p>
              <form className="space-y-2">
                <input
                  type="email"
                  placeholder="Ton email pro"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500"
                />
                <button
                  type="button"
                  className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                >
                  S'inscrire
                </button>
              </form>
            </SidebarBlock>

            <SidebarBlock title="Tags">
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/tags/${tag}`}
                    className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-indigo-300 hover:text-indigo-600"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </SidebarBlock>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Home;